// frontend/src/components/schedule/EventAuditLog.js
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { History, User, Clock, CheckCircle, XCircle, DollarSign, FileText, AlertCircle, Fuel, Trash2, Undo } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:8001';

const ACTION_ICONS = {
  check_in: { icon: CheckCircle, color: 'text-green-400', label: 'Check-in' },
  check_out: { icon: XCircle, color: 'text-red-400', label: 'Check-out' },
  billing: { icon: DollarSign, color: 'text-yellow-400', label: 'Facturation' },
  grading: { icon: FileText, color: 'text-blue-400', label: 'Évaluation' },
  squawk: { icon: AlertCircle, color: 'text-orange-400', label: 'Squawk' },
  fuel: { icon: Fuel, color: 'text-cyan-400', label: 'Carburant' }
};

const EventAuditLog = ({ eventId, onRefresh }) => {
  const { user, hasMinLevel } = useAuth();
  const isManager = hasMinLevel(75);
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deletingId, setDeletingId] = useState(null);

  const fetchLogs = async () => {
    if (!eventId) return;
    setLoading(true);
    try {
      const token = localStorage.getItem('access_token');
      const response = await axios.get(`${API_BASE}/schedule/events/${eventId}/audit`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setLogs(response.data || []);
    } catch (error) {
      console.error('Error fetching audit logs:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchLogs();
  }, [eventId]);

  const handleDeleteLog = async (auditId) => {
    if (!window.confirm('Supprimer cette action ? Cette opération est irréversible.')) return;
    
    setDeletingId(auditId);
    try {
      const token = localStorage.getItem('access_token');
      await axios.delete(`${API_BASE}/schedule/events/${eventId}/audit/${auditId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      await fetchLogs();
      if (onRefresh) onRefresh();
    } catch (error) {
      console.error('Error deleting audit log:', error);
      alert('Erreur lors de la suppression');
    } finally {
      setDeletingId(null);
    }
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleString();
  };

  if (loading) {
    return (
      <div className="flex justify-center py-4">
        <div className="animate-spin rounded-full h-4 w-4 sm:h-5 sm:w-5 border-b-2 border-blue-500" />
      </div>
    );
  }

  if (logs.length === 0) {
    return (
      <div className="text-center py-3 sm:py-4 text-gray-500 text-[10px] sm:text-xs">
        <History className="w-3.5 h-3.5 sm:w-4 sm:h-4 mx-auto mb-1 opacity-50" />
        Aucune action enregistrée
      </div>
    );
  }

  return (
    <div className="space-y-2 max-h-48 sm:max-h-60 overflow-y-auto">
      {logs.map((log) => {
        const actionConfig = ACTION_ICONS[log.action] || { icon: History, color: 'text-gray-400', label: log.action };
        const ActionIcon = actionConfig.icon;
        
        return (
          <div key={log.id} className="text-[10px] sm:text-xs p-1.5 sm:p-2 bg-gray-700/30 rounded-lg group">
            <div className="flex flex-wrap items-center justify-between gap-1">
              <div className="flex items-center gap-1.5 sm:gap-2">
                <ActionIcon className={`w-3 h-3 sm:w-3.5 sm:h-3.5 ${actionConfig.color}`} />
                <span className="text-gray-300 font-medium">{actionConfig.label}</span>
              </div>
              <div className="flex flex-wrap items-center gap-1 sm:gap-2">
                <span className="text-gray-500 text-[9px] sm:text-[10px] flex items-center gap-0.5 sm:gap-1">
                  <User className="w-2 h-2 sm:w-2.5 sm:h-2.5" />
                  {log.first_name} {log.last_name}
                </span>
                <span className="text-gray-500 text-[9px] sm:text-[10px] flex items-center gap-0.5 sm:gap-1">
                  <Clock className="w-2 h-2 sm:w-2.5 sm:h-2.5" />
                  {formatDate(log.created_at)}
                </span>
                {isManager && (
                  <button
                    onClick={() => handleDeleteLog(log.id)}
                    disabled={deletingId === log.id}
                    className="opacity-0 group-hover:opacity-100 p-0.5 sm:p-1 hover:bg-red-500/20 rounded transition text-red-400"
                    title="Supprimer (en cas d'erreur)"
                  >
                    {deletingId === log.id ? (
                      <div className="w-2.5 h-2.5 sm:w-3 sm:h-3 border-2 border-red-400 border-t-transparent rounded-full animate-spin" />
                    ) : (
                      <Trash2 className="w-2.5 h-2.5 sm:w-3 sm:h-3" />
                    )}
                  </button>
                )}
              </div>
            </div>
            <p className="text-gray-400 mt-1 text-[10px] sm:text-[11px] leading-relaxed break-words">
              {log.comment}
            </p>
            {log.old_value && log.new_value && (
              <p className="text-gray-500 text-[8px] sm:text-[10px] mt-1 break-words">
                {log.old_value} → {log.new_value}
              </p>
            )}
          </div>
        );
      })}
    </div>
  );
};

export default EventAuditLog;